import { c as _c } from "react/compiler-runtime";
import { CLICK_ID_STORAGE_KEYS, type PersistedClickIdKey, toClickIdFallbacks } from './click-id-fallbacks';
import { usePersistedCookieParam, usePersistedQueryParam } from './use-persisted-param';
import { usePersistedReferrerParams } from './use-persisted-referrer-params';
const UTM_STORAGE_KEYS = {
  source: '__utm_source',
  campaign: '__utm_campaign',
  medium: '__utm_medium',
  content: '__utm_content',
  term: '__utm_term',
  partner: '__partner',
  portfolioCode: '__portfolio_code',
  portfolioEntry: '__portfolio_entry',
  ctaLocation: '__cta_location'
} as const;

/**
 * Persisted params with no top-level field on the signup payload. They reach it only through
 * `search_params`, so on a stripped URL they ride the same fallback as the click IDs. `utm_term` is
 * the ad's keyword; `portfolio_entry` + `cta_location` name the portfolio landing arm and button
 * (`apps/portfolio`), which a paid click's own `utm_content` no longer carries.
 */
// A type alias, not an interface: `getAllowlistedSearchParams` takes an indexable record.
export type PersistedSearchParamFallbacks = Record<PersistedClickIdKey | 'cta_location' | 'portfolio_entry' | 'utm_term', string>;
interface UtmParamValues {
  campaign: string;
  content: string;
  ctaLocation: string;
  fbclid: string;
  gbraid: string;
  gclid: string;
  medium: string;
  msclkid: string;
  partner: string;
  portfolioCode: string;
  portfolioEntry: string;
  portfolioUrl: string | null;
  referrer: string;
  source: string;
  term: string;
  ttclid: string;
  ttp: string;
  wbraid: string;
}
interface UtmParamSetters {
  setCampaign: (value: string) => void;
  setContent: (value: string) => void;
  setCtaLocation: (value: string) => void;
  setFbclid: (value: string) => void;
  setGbraid: (value: string) => void;
  setGclid: (value: string) => void;
  setMedium: (value: string) => void;
  setMsclkid: (value: string) => void;
  setPartner: (value: string) => void;
  setPortfolioCode: (value: string) => void;
  setPortfolioEntry: (value: string) => void;
  setPortfolioUrl: (value: string) => void;
  setReferrer: (value: string) => void;
  setSource: (value: string) => void;
  setTerm: (value: string) => void;
  setTtclid: (value: string) => void;
  setTtp: (value: string) => void;
  setWbraid: (value: string) => void;
}
export interface UtmParams extends UtmParamValues, UtmParamSetters {
  clickIdFallbacks: Record<PersistedClickIdKey, string>;
  /** `clickIdFallbacks` plus the persisted keys with no top-level payload field; pass it to `getAllowlistedSearchParams`. */
  searchParamFallbacks: PersistedSearchParamFallbacks;
}

/**
 * URL + localStorage attribution params. URL wins; stored values survive
 * in-app navigation and OAuth redirects that strip the query string.
 */
export function useUtmParams() {
  const $ = _c(53);
  const {
    referrer,
    portfolioUrl,
    setReferrer,
    setPortfolioUrl
  } = usePersistedReferrerParams();
  const {
    value: source,
    setValue: setSource
  } = usePersistedQueryParam("utm_source", UTM_STORAGE_KEYS.source);
  const {
    value: campaign,
    setValue: setCampaign
  } = usePersistedQueryParam("utm_campaign", UTM_STORAGE_KEYS.campaign);
  const {
    value: medium,
    setValue: setMedium
  } = usePersistedQueryParam("utm_medium", UTM_STORAGE_KEYS.medium);
  const {
    value: content,
    setValue: setContent
  } = usePersistedQueryParam("utm_content", UTM_STORAGE_KEYS.content);
  const {
    value: term,
    setValue: setTerm
  } = usePersistedQueryParam("utm_term", UTM_STORAGE_KEYS.term);
  const {
    value: partner,
    setValue: setPartner
  } = usePersistedQueryParam("partner", UTM_STORAGE_KEYS.partner);
  const {
    value: portfolioCode,
    setValue: setPortfolioCode
  } = usePersistedQueryParam("portfolio_code", UTM_STORAGE_KEYS.portfolioCode);
  const {
    value: portfolioEntry,
    setValue: setPortfolioEntry
  } = usePersistedQueryParam("portfolio_entry", UTM_STORAGE_KEYS.portfolioEntry);
  const {
    value: ctaLocation,
    setValue: setCtaLocation
  } = usePersistedQueryParam("cta_location", UTM_STORAGE_KEYS.ctaLocation);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      cookieFallback: "ttclid"
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    value: ttclid,
    setValue: setTtclid
  } = usePersistedQueryParam("ttclid", CLICK_ID_STORAGE_KEYS.ttclid, t0);
  const {
    value: gclid,
    setValue: setGclid
  } = usePersistedQueryParam("gclid", CLICK_ID_STORAGE_KEYS.gclid);
  const {
    value: fbclid,
    setValue: setFbclid
  } = usePersistedQueryParam("fbclid", CLICK_ID_STORAGE_KEYS.fbclid);
  const {
    value: msclkid,
    setValue: setMsclkid
  } = usePersistedQueryParam("msclkid", CLICK_ID_STORAGE_KEYS.msclkid);
  const {
    value: gbraid,
    setValue: setGbraid
  } = usePersistedQueryParam("gbraid", CLICK_ID_STORAGE_KEYS.gbraid);
  const {
    value: wbraid,
    setValue: setWbraid
  } = usePersistedQueryParam("wbraid", CLICK_ID_STORAGE_KEYS.wbraid);
  const {
    value: ttp,
    setValue: setTtp
  } = usePersistedCookieParam("_ttp", CLICK_ID_STORAGE_KEYS.ttp);
  let t1;
  if ($[1] !== fbclid || $[2] !== gbraid || $[3] !== gclid || $[4] !== msclkid || $[5] !== ttclid || $[6] !== ttp || $[7] !== wbraid) {
    t1 = toClickIdFallbacks({
      ttclid,
      gclid,
      fbclid,
      msclkid,
      ttp,
      gbraid,
      wbraid
    });
    $[1] = fbclid;
    $[2] = gbraid;
    $[3] = gclid;
    $[4] = msclkid;
    $[5] = ttclid;
    $[6] = ttp;
    $[7] = wbraid;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  const clickIdFallbacks = t1;
  let t2;
  if ($[9] !== clickIdFallbacks || $[10] !== ctaLocation || $[11] !== portfolioEntry || $[12] !== term) {
    t2 = {
      ...clickIdFallbacks,
      utm_term: term,
      portfolio_entry: portfolioEntry,
      cta_location: ctaLocation
    };
    $[9] = clickIdFallbacks;
    $[10] = ctaLocation;
    $[11] = portfolioEntry;
    $[12] = term;
    $[13] = t2;
  } else {
    t2 = $[13];
  }
  let t3;
  if ($[14] !== campaign || $[15] !== clickIdFallbacks || $[16] !== content || $[17] !== ctaLocation || $[18] !== fbclid || $[19] !== gbraid || $[20] !== gclid || $[21] !== medium || $[22] !== msclkid || $[23] !== partner || $[24] !== portfolioCode || $[25] !== portfolioEntry || $[26] !== portfolioUrl || $[27] !== referrer || $[28] !== setCampaign || $[29] !== setContent || $[30] !== setCtaLocation || $[31] !== setFbclid || $[32] !== setGbraid || $[33] !== setGclid || $[34] !== setMedium || $[35] !== setMsclkid || $[36] !== setPartner || $[37] !== setPortfolioCode || $[38] !== setPortfolioEntry || $[39] !== setPortfolioUrl || $[40] !== setReferrer || $[41] !== setSource || $[42] !== setTerm || $[43] !== setTtclid || $[44] !== setTtp || $[45] !== setWbraid || $[46] !== source || $[47] !== t2 || $[48] !== term || $[49] !== ttclid || $[50] !== ttp || $[51] !== wbraid) {
    t3 = {
      referrer,
      portfolioUrl,
      source,
      campaign,
      medium,
      content,
      term,
      partner,
      portfolioCode,
      portfolioEntry,
      ctaLocation,
      ttclid,
      gclid,
      fbclid,
      msclkid,
      ttp,
      gbraid,
      wbraid,
      clickIdFallbacks,
      searchParamFallbacks: t2,
      setReferrer,
      setSource,
      setCampaign,
      setMedium,
      setContent,
      setTerm,
      setPartner,
      setPortfolioCode,
      setPortfolioEntry,
      setCtaLocation,
      setPortfolioUrl,
      setTtclid,
      setGclid,
      setFbclid,
      setMsclkid,
      setTtp,
      setGbraid,
      setWbraid
    };
    $[14] = campaign;
    $[15] = clickIdFallbacks;
    $[16] = content;
    $[17] = ctaLocation;
    $[18] = fbclid;
    $[19] = gbraid;
    $[20] = gclid;
    $[21] = medium;
    $[22] = msclkid;
    $[23] = partner;
    $[24] = portfolioCode;
    $[25] = portfolioEntry;
    $[26] = portfolioUrl;
    $[27] = referrer;
    $[28] = setCampaign;
    $[29] = setContent;
    $[30] = setCtaLocation;
    $[31] = setFbclid;
    $[32] = setGbraid;
    $[33] = setGclid;
    $[34] = setMedium;
    $[35] = setMsclkid;
    $[36] = setPartner;
    $[37] = setPortfolioCode;
    $[38] = setPortfolioEntry;
    $[39] = setPortfolioUrl;
    $[40] = setReferrer;
    $[41] = setSource;
    $[42] = setTerm;
    $[43] = setTtclid;
    $[44] = setTtp;
    $[45] = setWbraid;
    $[46] = source;
    $[47] = t2;
    $[48] = term;
    $[49] = ttclid;
    $[50] = ttp;
    $[51] = wbraid;
    $[52] = t3;
  } else {
    t3 = $[52];
  }
  return t3;
}